import React, {useRef, useState} from 'react';
import {Alert, Button} from "react-bootstrap";
import {Link, useLocation, useNavigate, useParams} from "react-router-dom";
import {WhiteCard} from "../_components/WhiteCard";
import {useGetJson, useLongPress, useSelect, useTouch} from "../../../hooks";
import {capitalizeFirstLetter, endpoints, repeatType, timeUTCMinToLocalString} from "../../../utils2";
import {useAuth} from "../../../context";
import {EventType1, EventType2, RelayAction} from "../../../utils2/maps";
import {MenuClearSelectButton, MenuConfirmDeleteButton} from "../../_components";
import {RenderControlledBy, RenderDailyShort} from "../_components/UsefulComponents";
import {ABOverlayTooltip} from "../../_components/CustomOverlayTooltip";

export function ConfigurationCard({configure, events, onDelete}) {
    const {uid, did} = useParams()
    const location = useLocation()
    const navigate = useNavigate()

    const [selList, toggleSelectItems, clearSelectItems, isItemSelected] = useSelect(events)

    const ButtonList = [
        {id: EventType1.Automation, name: "Automation", icon: "bi bi-arrow-repeat", disabled: false, path: "time/new", tip: "Set Automation"},
        {id: EventType1.ControlledBy, name: "Controlled-By", icon: "bi bi-hdd-network", disabled: false, path: "cnrt/new", tip: "Set Controller Device"},
    ]

    function handleClick(id) {
        if (id === EventType1.Automation) {
            const steps = [`/device/${uid}/${did}/time/new`, `/device/${uid}/${did}/${configure.path}/new`, location.pathname]
            navigate(steps[0], {
                state: {
                    steps: steps,
                    index: configure.index,
                    event_type2: configure.event_type2
                }
            })
        } else if (id === EventType1.ControlledBy) {
            const steps = [`/device/${uid}/${did}/cntr/new`, `/device/${uid}/${did}/${configure.path}/new`, location.pathname]
            navigate(steps[0], {
                state: {
                    steps: steps,
                    // event_type: configure.event_type,
                    // title: configure.title
                }
            })
        }
    }

    return <>
        <WhiteCard>
            <div className=" d-flex align-items-center pb-1 border-bottom">
                <div className="fw-semibold fs-5">{configure.title}</div>
                <div className="d-flex ms-auto gap-2">
                    {ButtonList.map((item, index) => {
                        const isDisabled =
                            events.find(e => e.event_type1 !== item.id) ||
                            events.find(e => e.event_type1 === EventType1.ControlledBy);

                        const button = (
                            <Button
                                variant={item.variant}
                                onClick={() => handleClick(item.id)}
                                disabled={isDisabled}
                            >
                                <span className="d-none d-lg-block">{item.name}</span>
                                <span className={`d-lg-none ${item.icon}`}></span>
                            </Button>
                        )

                        return isDisabled ? (
                            <ABOverlayTooltip key={index} tip="A controlled event already exists"><span>{button}</span></ABOverlayTooltip>
                        ) : (

                            <ABOverlayTooltip key={index} tip={item.tip}><span>{button}</span></ABOverlayTooltip>
                        );
                    })}

                    {selList.length > 0 &&
                        <>
                            <MenuClearSelectButton hide={selList.length === 0} onClick={clearSelectItems}>
                                {selList.length} {selList.length === 1 ? "item" : "items"}
                            </MenuClearSelectButton>
                            <MenuConfirmDeleteButton hide={selList.length === 0} onSubmit={() => onDelete(selList)}>
                                {selList.length} {selList.length === 1 ? "event" : "events"}
                            </MenuConfirmDeleteButton>
                        </>}
                </div>
            </div>
            <div>
                {events && events.length > 0 ?
                    events.map((event, index) =>
                        <RenderEvent key={index} eventId={event.id} selected={isItemSelected(event.id)} onSelect={toggleSelectItems} selectedCount={selList.length}/>
                    ) :
                    <div>No settings have been configured yet</div>
                }
            </div>
        </WhiteCard>
    </>
}

function RenderEvent({eventId, selected, selectedCount, onSelect}) {
    const {uid, did} = useParams()

    const {authToken} = useAuth()

    const touch = useTouch()
    const ref = useRef(null)
    useLongPress(ref, () => onSelect(eventId))

    const [ev, loadingEvent, errorEvent, clearErrorEvent] = useGetJson(endpoints.event.get(eventId), authToken?.jwt)
    const event = ev?.data

    const config1 = event?.config1
    const config2 = event?.config2

    const truncate = (str, max = 20) => str?.length > max ? `${str.substring(0, max)}...` : str

    const toEditEventType1 = event?.event_type1 === EventType1.Automation ? `/device/${uid}/${did}/time/${event?.id}` : `/device/${uid}/${did}/cntr/${event?.id}`
    const toEditEventType2 = (event?.event_type2 === EventType2.Playback || event?.event_type2 === EventType2.Sender) ?
        `/device/${uid}/${did}/${event?.event_type2}/${event?.id}` :
        `/device/${uid}/${did}/${event?.event_type2}${config2?.index}/${event?.id}`

    return (
        <div ref={ref} className={`d-flex align-items-center gap-2 p-1 mb-2 rounded ${(selected ? "bg-secondary-subtle" : "hover-bg-secondary-subtle")}`}>

            <Alert variant="danger" show={errorEvent} dismissible onClose={clearErrorEvent}>{errorEvent}</Alert>

            <div className="d-flex w-100 flex-column flex-sm-row align-items-sm-center">
                <div>
                    <Button variant="link" as={Link} to={toEditEventType1}
                            className="p-0 flex-shrink-0 link-body-emphasis link-underline link-underline-opacity-0 link-underline-opacity-75-hover link-offset-1 link-offset-1-hover">
                        <span className="fw-medium">{truncate(event?.name, 30)}</span>
                    </Button>
                    {event?.event_type1 === EventType1.Automation && (
                        <div className="d-flex">
                            {config1.repeat === repeatType.Daily ? (
                                <RenderDailyShort config1={config1} />
                            ) : (
                                <div>{`${capitalizeFirstLetter(config1.weekly_start.slice(0, 3))} - ${capitalizeFirstLetter(config1.weekly_stop.slice(0, 3))}`}</div>
                            )}
                            <div className="me-1">;</div>
                            <div>
                                {`${timeUTCMinToLocalString(config1?.time_start)} - ${timeUTCMinToLocalString(config1?.time_stop)}`}
                            </div>
                        </div>
                    )}


                    {/*<div className="fw-light">{capitalizeFirstLetter(config1?.repeat)}: {timeUTCMinToLocalString(config1?.time_start)} - {timeUTCMinToLocalString(config1?.time_stop)}</div>}*/}
                    {event?.event_type1 === EventType1.ControlledBy && <RenderControlledBy config1={config1}/>}
                </div>
                <div className="ms-sm-auto">
                    <Button variant="link" as={Link} to={toEditEventType2}
                            className="p-0 flex-shrink-0 link-body-emphasis link-underline link-underline-opacity-0 link-underline-opacity-75-hover link-offset-1 link-offset-1-hover">
                        {event?.event_type2 === EventType2.Playback && <span className="">{truncate(config2?.main_host, 30)}</span>}
                        {event?.event_type2 === EventType2.Sender && <span className="">{truncate(config2?.uri, 30)}</span>}
                        {event?.event_type2 === EventType2.Relay && <span className="">{RelayAction[config2?.action].name}</span>}
                    </Button>
                </div>
            </div>

            <div className="ms-auto">
                {(!touch || selected || selectedCount > 0) &&
                    <Button className="link-underline link-underline-opacity-0" variant="link"
                            onClick={() => onSelect(eventId)}>
                        <span className={`bi ${selected ? "bi-check-square" : "bi-square"}`}></span>
                    </Button>
                }
            </div>
        </div>

    )
}


